<template>
  <v-menu
    v-model="isOpen"
    :position-x="positionX"
    :position-y="positionY"
    absolute
    offset-y
    :close-on-content-click="false"
    :close-on-click="false"
    :z-index="15"
    data-testid="pie-chart-popup"
  >
    <v-card v-if="trap" class="pie-chart-popup-card" elevation="4">
      <v-card-title class="py-2 text-subtitle-1">
        {{ trap.trapNo }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-3">
        <div class="d-flex align-center">
          <!-- パイチャート（SVG） -->
          <svg
            :width="chartSize"
            :height="chartSize"
            :viewBox="`0 0 ${chartSize} ${chartSize}`"
            class="pie-chart-svg"
          >
            <g :transform="`translate(${chartSize / 2}, ${chartSize / 2})`">
              <!-- パイセグメント -->
              <path
                v-for="segment in pieSegments"
                :key="segment.type"
                :d="segment.path"
                :fill="segment.color"
                data-testid="pie-segment"
              />
              <!-- 十字の白線（セグメント境界） -->
              <line x1="0" y1="0" :x2="0" :y2="-chartRadius" stroke="white" stroke-width="2" />
              <line x1="0" y1="0" :x2="chartRadius" :y2="0" stroke="white" stroke-width="2" />
              <line x1="0" y1="0" :x2="0" :y2="chartRadius" stroke="white" stroke-width="2" />
              <line x1="0" y1="0" :x2="-chartRadius" :y2="0" stroke="white" stroke-width="2" />
            </g>
          </svg>
          <!-- 凡例と捕獲数 -->
          <div class="legend-section ml-3">
            <div
              v-for="item in legendItems"
              :key="item.type"
              class="legend-item d-flex align-center mb-1"
            >
              <span
                class="legend-color mr-2"
                :style="{ backgroundColor: item.color }"
              />
              <span class="legend-label">{{ item.label }}:</span>
              <span class="legend-count ml-1 font-weight-medium">{{ item.countDisplay }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<i18n lang="yaml">
ja:
  current: 当月
  previous: 前月
  lastYear: 昨年同月
  lastYearPrevious: 昨年前月

en:
  current: Current Month
  previous: Previous Month
  lastYear: Same Month Last Year
  lastYearPrevious: Previous Month Last Year
</i18n>

<script>
const PIE_SEGMENTS = [
  { type: 'current', startAngle: -90, endAngle: 0 },
  { type: 'previous', startAngle: 0, endAngle: 90 },
  { type: 'lastYearPrevious', startAngle: 90, endAngle: 180 },
  { type: 'lastYear', startAngle: 180, endAngle: 270 },
];

const SEGMENT_COLORS = {
  current: '#CC0000',
  previous: '#D28181',
  lastYear: '#666666',
  lastYearPrevious: '#D9D9D9',
};

// 凡例の表示順序（時系列順）
const LEGEND_ORDER = ['current', 'previous', 'lastYear', 'lastYearPrevious'];

export default {
  name: 'PieChartPopup',
  props: {
    trap: {
      type: Object,
      default: null,
    },
    positionX: {
      type: Number,
      default: 0,
    },
    positionY: {
      type: Number,
      default: 0,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartSize: 104,
      chartRadius: 50,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    countMap() {
      if (!this.trap) return {};
      return {
        current: this.trap.count,
        previous: this.trap.previousMonthCount,
        lastYear: this.trap.lastYearMonthCount,
        lastYearPrevious: this.trap.lastYearPreviousMonthCount,
      };
    },
    pieSegments() {
      if (!this.trap) return [];

      return PIE_SEGMENTS
        .filter((seg) => {
          const count = this.countMap[seg.type];
          return count != null && count > 0;
        })
        .map((seg) => {
          const count = this.countMap[seg.type];
          const radius = this.calculateRadius(count);
          const path = this.createPieSlicePath(radius, seg.startAngle, seg.endAngle);

          return {
            type: seg.type,
            color: SEGMENT_COLORS[seg.type],
            path,
          };
        });
    },
    legendItems() {
      return LEGEND_ORDER.map((type) => {
        const count = this.countMap[type];
        return {
          type,
          label: this.$t(type),
          color: SEGMENT_COLORS[type],
          countDisplay: count === null || count === undefined ? '---' : count,
        };
      });
    },
  },
  methods: {
    calculateRadius(count) {
      if (count === 0) return 10;
      const minRadius = 10;
      const maxRadius = this.chartRadius;
      const radiusRange = maxRadius - minRadius;
      const scaleFactor = radiusRange / Math.log10(1000);
      const radius = minRadius + Math.log10(count + 1) * scaleFactor;
      return Math.min(radius, maxRadius);
    },
    createPieSlicePath(radius, startAngle, endAngle) {
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = radius * Math.cos(startRad);
      const y1 = radius * Math.sin(startRad);
      const x2 = radius * Math.cos(endRad);
      const y2 = radius * Math.sin(endRad);

      const largeArcFlag = 0;

      return `M 0 0 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
    },
  },
};
</script>

<style scoped>
.pie-chart-popup-card {
  min-width: 220px;
  width: fit-content;
  padding-top: 40px; /* タイトル用のスペース確保 */
  position: relative;
}

::v-deep .pie-chart-popup-card .v-card__title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.pie-chart-svg {
  flex-shrink: 0;
}

.legend-section {
  font-size: 12px;
}

.legend-item {
  line-height: 1.4;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-label {
  color: #666;
  white-space: nowrap;
}

.legend-count {
  color: #333;
}
</style>
